/**
 * SCADA Service - API communication for plant monitoring data
 */

import {
  SCADATag, SCADATagResponse, SCADAValue, SCADAValueResponse,
  SCADAAlarm, SCADAAlarmResponse, SiteDashboard, AlarmSummary,
  SCADAHistoricalData, PlantOverviewImage, PlantConfiguration,
  SCADATagUpdate, ConfigurationSaveResponse, ImageUploadResponse,
  BulkUpdateResponse, TagPositionUpdate, EquipmentOption,
  HistorianQueryParams, HistorianResponse, DashboardOverviewResponse,
  SCADAWriteRequest, SCADAWriteResponse, SCADAWriteLogEntry,
  WriteSessionRequest, WriteSessionResponse, WriteSessionStatus,
  TOTPSetupRequest, TOTPSetupResponse, TOTPVerificationRequest,
  TOTPVerificationResponse, TOTPDevice
} from '../types/scada';

// Global process declaration for TypeScript
declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Global CSRF token cache
let csrfToken: string | null = null;

/**
 * Get CSRF token from backend
 */
const getCSRFToken = async (): Promise<string> => {
  if (csrfToken) return csrfToken;
  
  try {
    const response = await fetch(`${API_BASE_URL}/users/auth/csrf_token/`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token;
      return csrfToken!;
    }
  } catch (error) {
    console.warn('Failed to get CSRF token:', error);
  }
  
  return '';
};

/**
 * Generic API request helper
 */
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add CSRF token for non-GET requests
  if (options.method && options.method !== 'GET') {
    const token = await getCSRFToken();
    if (token) {
      defaultHeaders['X-CSRFToken'] = token;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error ${response.status}: ${errorText}`);
  }

  return await response.json();
};

/**
 * SCADA Tags API
 */
export const scadaTagsAPI = {
  /**
   * Get all SCADA tags with optional filtering
   */
  getAll: async (params: {
    site_id?: number;
    equipment?: number;
    data_type?: string;
    is_displayed_on_overview?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<SCADATagResponse> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/sites/scada/tags/${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest<SCADATagResponse>(endpoint);
  },

  /**
   * Get a specific SCADA tag
   */
  getById: async (id: number): Promise<SCADATag> => {
    return apiRequest<SCADATag>(`/sites/scada/tags/${id}/`);
  },

  /**
   * Get current value for a tag
   */
  getCurrentValue: async (id: number): Promise<{
    tag_name: string;
    current_value: number | null;
    current_value_display: string;
    alarm_status: string;
    timestamp: string;
  }> => {
    return apiRequest(`/sites/scada/tags/${id}/current_value/`);
  },

  /**
   * Get historical data for a tag
   */
  getHistoricalData: async (
    id: number,
    params: { hours?: number; limit?: number } = {}
  ): Promise<SCADAHistoricalData> => {
    const searchParams = new URLSearchParams();
    if (params.hours) searchParams.append('hours', params.hours.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    
    const queryString = searchParams.toString();
    const endpoint = `/sites/scada/tags/${id}/historical_data/${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest<SCADAHistoricalData>(endpoint);
  },

  /**
   * Get tags configured for overview display
   */
  getOverviewTags: async (siteId: number): Promise<SCADATag[]> => {
    return apiRequest<SCADATag[]>(`/sites/scada/tags/overview/?site_id=${siteId}`);
  },

  /**
   * Get lightweight dashboard overview (current values only)
   */
  getDashboardOverview: async (siteId: number): Promise<DashboardOverviewResponse> => {
    return apiRequest(`/sites/scada/tags/dashboard_overview/?site_id=${siteId}`);
  },

  /**
   * Get optimized historical data for a specific tag
   */
  getTagHistoricalData: async (
    tagId: number,
    params: {
      hours?: number;
      limit?: number;
      resolution?: 'auto' | 'raw' | '5min' | '15min' | '1hour';
    } = {}
  ): Promise<{
    tag_id: number;
    tag_name: string;
    description: string;
    engineering_unit: string;
    data_points: Array<{
      timestamp: string;
      value: number;
      quality: string;
      engineering_unit: string;
    }>;
    total_points: number;
    time_range: {
      start: string;
      end: string;
      hours: number;
    };
    resolution: string;
  }> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/sites/scada/tags/${tagId}/historical_data/${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest(endpoint);
  },
};

/**
 * SCADA Values API
 */
export const scadaValuesAPI = {
  /**
   * Get historical values with filtering
   */
  getAll: async (params: {
    tag_id?: number;
    hours?: number;
    quality?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<SCADAValueResponse> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/sites/scada/values/${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest<SCADAValueResponse>(endpoint);
  },
};

/**
 * SCADA Alarms API
 */
export const scadaAlarmsAPI = {
  /**
   * Get all alarms with filtering
   */
  getAll: async (params: {
    site_id?: number;
    state?: string;
    priority?: string;
    active_only?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<SCADAAlarmResponse> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/sites/scada/alarms/${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest<SCADAAlarmResponse>(endpoint);
  },

  /**
   * Acknowledge an alarm
   */
  acknowledge: async (id: number, comment: string = ''): Promise<{
    message: string;
    alarm_id: number;
    acknowledged_by: string;
    acknowledged_at: string;
  }> => {
    return apiRequest(`/sites/scada/alarms/${id}/acknowledge/`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  },

  /**
   * Get active alarm summary
   */
  getActiveSummary: async (siteId?: number): Promise<AlarmSummary> => {
    const endpoint = `/sites/scada/alarms/active_summary/${siteId ? `?site_id=${siteId}` : ''}`;
    return apiRequest<AlarmSummary>(endpoint);
  },
};

/**
 * Site Dashboard API
 */
export const siteDashboardAPI = {
  /**
   * Get enhanced site dashboard with SCADA data
   */
  getAll: async (): Promise<{ results: SiteDashboard[] }> => {
    return apiRequest<{ results: SiteDashboard[] }>('/sites/dashboard/');
  },

  /**
   * Get specific site dashboard
   */
  getById: async (id: number): Promise<SiteDashboard> => {
    return apiRequest<SiteDashboard>(`/sites/dashboard/${id}/`);
  },
};

/**
 * Plant Overview Images API
 */
export const plantOverviewAPI = {
  /**
   * Get overview images for a site
   */
  getBySite: async (siteId: number): Promise<PlantOverviewImage[]> => {
    return apiRequest<{ results: PlantOverviewImage[] }>(`/sites/scada/overview-images/?site=${siteId}`)
      .then(response => response.results);
  },

  /**
   * Get active overview image for a site
   */
  getActiveBySite: async (siteId: number): Promise<PlantOverviewImage | null> => {
    const images = await plantOverviewAPI.getBySite(siteId);
    return images.find(img => img.is_active) || null;
  },

  /**
   * Upload new plant overview image
   */
  upload: async (siteId: number, imageFile: File, name: string): Promise<ImageUploadResponse> => {
    const formData = new FormData();
    formData.append('site_id', siteId.toString());
    formData.append('image', imageFile);
    formData.append('name', name);

    return apiRequest<ImageUploadResponse>('/sites/scada/overview-images/upload_image/', {
      method: 'POST',
      body: formData,
      // Don't set Content-Type for FormData
      headers: {}
    });
  },

  /**
   * Set image as active
   */
  setActive: async (imageId: number): Promise<PlantOverviewImage> => {
    return apiRequest<{ image: PlantOverviewImage }>(`/sites/scada/overview-images/${imageId}/set_active/`, {
      method: 'POST'
    }).then(response => response.image);
  }
};

/**
 * Combined SCADA API for convenience
 */
export const scadaAPI = {
  /**
   * Get SCADA tags
   */
  getTags: scadaTagsAPI.getAll,
  
  /**
   * Get tag by ID
   */
  getTag: scadaTagsAPI.getById,
  
  /**
   * Get tag historical data
   */
  getTagHistory: async (tagId: number, options: { hours?: number } = {}): Promise<SCADAHistoricalData> => {
    const hours = options.hours || 24;
    return apiRequest<SCADAHistoricalData>(`/sites/scada/tags/${tagId}/historical_data/?hours=${hours}`);
  },
  
  /**
   * Get values
   */
  getValues: scadaValuesAPI.getAll,
  
  /**
   * Get alarms
   */
  getAlarms: scadaAlarmsAPI.getAll
};


/**
 * Real-time data updates (for future WebSocket implementation)
 */
export class SCADARealtimeService {
  private ws: WebSocket | null = null;
  private listeners: Map<string, ((data: any) => void)[]> = new Map();

  connect(siteId: number): void {
    // TODO: Implement WebSocket connection for real-time updates
    // const wsUrl = `ws://localhost:8765/scada/?site_id=${siteId}`;
    console.log('Real-time SCADA connection not yet implemented');
  }

  subscribe(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  unsubscribe(event: string, callback: (data: any) => void): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

// Export singleton instance for real-time service
export const scadaRealtimeService = new SCADARealtimeService();

// Utility functions
export const scadaUtils = {
  /**
   * Get alarm priority color
   */
  getAlarmPriorityColor: (priority: string): string => {
    switch (priority) {
      case 'CRITICAL': return '#d32f2f';
      case 'HIGH': return '#f57c00';
      case 'MEDIUM': return '#fbc02d';
      case 'LOW': return '#388e3c';
      default: return '#9e9e9e';
    }
  },

  /**
   * Get alarm status color
   */
  getAlarmStatusColor: (status: string): string => {
    switch (status) {
      case 'HIGH_ALARM': return '#d32f2f';
      case 'LOW_ALARM': return '#f57c00';
      case 'BOOLEAN_TRUE_ALARM': return '#d32f2f';
      case 'BOOLEAN_FALSE_ALARM': return '#f57c00';
      case 'NORMAL': return '#388e3c';
      case 'NO_DATA': return '#9e9e9e';
      case 'ALARMS_DISABLED': return '#388e3c'; // Treat as normal when alarms are disabled
      default: return '#388e3c'; // Default to normal green instead of grey
    }
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp: (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  },

  /**
   * Check if alarm is active
   */
  isAlarmActive: (alarm: SCADAAlarm): boolean => {
    return alarm.state === 'ACTIVE';
  },

  /**
   * Multi-tag historian query for plotting multiple tags over time
   */
  queryHistorian: async (params: HistorianQueryParams): Promise<HistorianResponse> => {
    try {
      const response = await apiRequest<any>(`/sites/scada/tags/historian/`, {
        method: 'POST',
        body: JSON.stringify(params),
      });

      // Map backend response to frontend interface
      // Backend sends 'tags' but frontend expects 'data'
      return {
        success: true,
        data: response.tags || [],
        query_info: response.query_info || {},
        errors: response.errors || []
      };
    } catch (error) {
      console.error('Historian query failed:', error);
      throw error;
    }
  },

  /**
   * Get available SCADA tags for historian selection
   */
  getAvailableTags: async (siteId?: number): Promise<SCADATag[]> => {
    try {
      // Use a high page_size to get all tags, or we could implement proper pagination
      const url = siteId 
        ? `/sites/scada/tags/?site_id=${siteId}&ordering=tag_name&page_size=1000`
        : '/sites/scada/tags/?ordering=tag_name&page_size=1000';
      
      const data = await apiRequest<SCADATagResponse>(url);
      
      return data.results || [];
    } catch (error) {
      console.error('Failed to fetch available tags:', error);
      throw error;
    }
  },

  /**
   * Get lightweight dashboard overview (current values only)
   */
  getDashboardOverview: async (siteId: number): Promise<{
    tags: Array<{
      id: number;
      tag_name: string;
      description: string;
      equipment_name: string | null;
      engineering_unit: string;
      current_value: number | null;
      current_value_display: string;
      alarm_status: string;
      timestamp: string | null;
      overview_position_x: number | null;
      overview_position_y: number | null;
      link_to_area: string;
    }>;
    total_tags: number;
    timestamp: string;
  }> => {
    return apiRequest(`/sites/scada/tags/dashboard_overview/?site_id=${siteId}`);
  },

  /**
   * Get optimized historical data for a specific tag
   */
  getTagHistoricalData: async (
    tagId: number,
    params: {
      hours?: number;
      limit?: number;
      resolution?: 'auto' | 'raw' | '5min' | '15min' | '1hour';
    } = {}
  ): Promise<{
    tag_id: number;
    tag_name: string;
    description: string;
    engineering_unit: string;
    data_points: Array<{
      timestamp: string;
      value: number;
      quality: string;
      engineering_unit: string;
    }>;
    total_points: number;
    time_range: {
      start: string;
      end: string;
      hours: number;
    };
    resolution: string;
  }> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/sites/scada/tags/${tagId}/historical_data/${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest(endpoint);
  },
}; 